import type { NavigationGuardWithThis } from 'vue-router'
import { isClient, safeSessionStorage } from '@/composables/useSSR'

/**
 * Account tokens that arrive in an emailed link (password reset, email
 * verification, sub-account invite).
 *
 * Stripe.js, Matomo and Google Ads read document.location once they load, which
 * is after @/bootstrap/scrubUrlToken moves the token here at boot: the Stripe
 * loader is microtask-deferred and the boot graph has no top-level await. A
 * route's beforeEnter covers in-app navigations; the view reads it from here.
 */
export const URL_TOKEN_KEYS = ['verifyEmail', 'resetPassword', 'inviteAccept'] as const
export type UrlTokenKey = (typeof URL_TOKEN_KEYS)[number]

const STORAGE_PREFIX = 'itsh_url_token:'
const TTL_MS = 30 * 60 * 1000

interface StashEntry {
  token: string
  exp: number
}

// sessionStorage keeps a reload of the clean URL working in the same tab. The
// map covers storage that is blocked or throws, for the life of the page.
const memory = new Map<UrlTokenKey, StashEntry>()

function firstString(value: unknown): string | null {
  const first = Array.isArray(value) ? value[0] : value
  return typeof first === 'string' && first !== '' ? first : null
}

function liveToken(entry: unknown): string | null {
  if (typeof entry !== 'object' || entry === null) return null
  const { token, exp } = entry as Partial<StashEntry>
  return typeof exp === 'number' && exp > Date.now() ? firstString(token) : null
}

export function stashUrlToken(key: UrlTokenKey, token: string): void {
  // Module state is shared across requests on the SSR server.
  if (!isClient) return
  const entry: StashEntry = { token, exp: Date.now() + TTL_MS }
  memory.set(key, entry)
  try {
    const storage = safeSessionStorage()
    // Removed first so a failing setItem cannot leave an older token behind.
    storage?.removeItem(STORAGE_PREFIX + key)
    storage?.setItem(STORAGE_PREFIX + key, JSON.stringify(entry))
  } catch {
    // The in-memory copy still serves this page life.
  }
}

/**
 * The URL value if any (SSR, or a same-route navigation that skipped
 * beforeEnter: the newest link), else the stashed one. The stash is kept so a
 * reload finds it; call clearUrlToken once the backend has used or rejected it.
 */
export function readUrlToken(key: UrlTokenKey, fromUrl?: unknown): string | null {
  const urlToken = firstString(fromUrl)
  if (urlToken) {
    stashUrlToken(key, urlToken)
    return urlToken
  }
  const inMemory = liveToken(memory.get(key))
  if (inMemory) return inMemory
  memory.delete(key)
  let stored: string | null = null
  try {
    const raw = safeSessionStorage()?.getItem(STORAGE_PREFIX + key)
    if (raw) stored = liveToken(JSON.parse(raw))
  } catch {
    // Unreadable or unparseable: dropped below.
  }
  if (!stored) clearUrlToken(key)
  return stored
}

export function clearUrlToken(key: UrlTokenKey): void {
  memory.delete(key)
  try {
    safeSessionStorage()?.removeItem(STORAGE_PREFIX + key)
  } catch {
    // Nothing persisted to remove.
  }
}

export function clearAllUrlTokens(): void {
  for (const key of URL_TOKEN_KEYS) clearUrlToken(key)
}

/** beforeEnter for a route that takes the token as `?token=`. */
export function stashQueryToken(key: UrlTokenKey): NavigationGuardWithThis<undefined> {
  return (to) => {
    if (!isClient || !('token' in to.query)) return
    const token = firstString(to.query.token)
    if (token) stashUrlToken(key, token)
    const query = { ...to.query }
    delete query.token
    return { name: to.name, params: to.params, query, hash: to.hash, replace: true }
  }
}

/** beforeEnter for a route that takes the token as the optional `:token?` param. */
export function stashParamToken(key: UrlTokenKey): NavigationGuardWithThis<undefined> {
  return (to) => {
    const token = firstString(to.params.token)
    if (!isClient || !token) return
    stashUrlToken(key, token)
    const params = { ...to.params }
    delete params.token
    return { name: to.name, params, query: to.query, hash: to.hash, replace: true }
  }
}
